<template>
  <div
    class="file-uploader"
    :class="{ 'drag-over': isDragOver }"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <input
      ref="fileInput"
      type="file"
      accept="application/pdf"
      multiple
      hidden
      @change="onFileSelect"
    />
    <button class="btn btn-primary mb-3" @click="triggerFileInput">
      Select files
    </button>
    <p>Drag and drop files here to upload.</p>
    <ul class="file-uploader__list">
      <li v-for="(file, idx) in files" :key="idx" class="file-uploader__file">
        <span v-if="file.error">{{ file.error }}</span>

        <input
          v-if="file.isEditing"
          v-model="file.newName"
          class="form-control form-control-sm"
          @keyup.enter="onFileNameChange(file)"
        />
        <div v-else class="file-uploader__file-name">{{ file.name }}</div>
        <div class="file-uploader__file-btns">
          <button
            v-if="file.status === FILE_STATUS.UPLOADED && !file.isEditing"
            class="btn btn-primary btn-sm ml-2 align-self-center"
            @click="
              file.isEditing = true;
              file.newName = file.name;
            "
          >
            Edit
          </button>
          <button
            v-if="file.isEditing"
            class="btn btn-success btn-sm ml-2 align-self-center"
            @click="onFileNameChange(file)"
          >
            Confirm
          </button>
          <button
            class="btn btn-danger btn-sm ml-2 align-self-center"
            @click="
              file.status === FILE_STATUS.PENDING
                ? onFileUploadCancel(file)
                : onFileRemove(idx)
            "
          >
            {{ file.status === FILE_STATUS.PENDING ? 'Cancel' : 'Remove' }}
          </button>
        </div>
        <progress-bar
          v-if="file.isProgressVisible && !file.error"
          class="file-uploader__file-progress"
          :progress="file.progress"
          :progress-text="String(file.progress) + '%'"
        />
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ProgressBar from '@/components/ProgressBar.vue';
import { renameFile, uploadFile } from '@/common/api';
import { FileUploadResponse } from '@/types/FileUploadResponse';
import axios, { AxiosResponse } from 'axios';
import { FileData } from '@/types/FileData';
import { FILE_STATUS } from '@/common/constants';
import { FileUploadState } from '@/types/FileUploadState';
import { FileMetadata } from '@/types/FileMetadata';
import { FileEditingState } from '@/types/FileEditingState';
import { UploadedFile } from '@/types/UploadedFile';

export default defineComponent({
  name: 'FileUpload',

  components: { ProgressBar },

  emits: {
    'files-ready': (files: UploadedFile[]) => files.length > 0,
    'files-not-ready': () => true,
  },

  data() {
    return {
      files: [] as FileData[],
      isDragOver: false,
    };
  },
  computed: {
    FILE_STATUS() {
      return FILE_STATUS;
    },
  },

  methods: {
    triggerFileInput() {
      const input = this.$refs.fileInput as HTMLInputElement;
      input.value = '';
      input.click();
    },

    onFileSelect(event: Event) {
      const target = event.target as HTMLInputElement;
      if (!target.files) {
        return;
      }
      Array.from(target.files).forEach(this.uploadFile);
    },

    onDragOver(event: DragEvent) {
      event.dataTransfer!.dropEffect = 'copy';
      this.isDragOver = true;
    },

    onDragLeave() {
      this.isDragOver = false;
    },

    onDrop(event: DragEvent) {
      if (!event.dataTransfer?.files) {
        return;
      }

      event.preventDefault();
      this.isDragOver = false;

      const files = Array.from(event.dataTransfer.files);
      const pdfFiles = files.filter((file) => file.type === 'application/pdf');

      if (pdfFiles.length === 0) {
        alert('Only PDF files are available for uploading.');
        return;
      }

      if (pdfFiles.length !== files.length) {
        alert(
          'There are some non-PDF files. Only PDF files were allowed to be uploaded.',
        );
      }

      pdfFiles.forEach(this.uploadFile);
    },

    checkIfFilesReady() {
      const uploadedFiles = this.files.filter(
        (file) => file.status === FILE_STATUS.UPLOADED,
      ) as UploadedFile[];
      if (
        uploadedFiles.length === 0 ||
        this.files.length !== uploadedFiles.length
      ) {
        this.$emit('files-not-ready');
        return;
      }

      this.$emit('files-ready', uploadedFiles);
    },

    uploadFile(file: File) {
      this.$emit('files-not-ready');

      const fileIdx =
        this.files.push({
          id: null,
          name: file.name,
          progress: 0,
          status: FILE_STATUS.PENDING,
          error: null,
          abortController: null,
          isEditing: false,
          newName: '',
          isProgressVisible: true,
        }) - 1;

      const fileData = this.files[fileIdx];

      const formData = new FormData();
      formData.append('file', file);

      const { request, abortController } = uploadFile(
        formData,
        (currentProgress) => {
          fileData.progress = currentProgress;
        },
      );

      fileData.abortController = abortController;

      request
        .then((response: AxiosResponse<FileUploadResponse>) => {
          fileData.id = response.data.id;
          fileData.name = response.data.name;
          fileData.status = FILE_STATUS.UPLOADED;

          // Delay to avoid UI blinking, ensuring a smooth transition.
          setTimeout(() => {
            fileData.isProgressVisible = false;
          }, 1500);
        })
        .catch((error: unknown) => {
          if (axios.isCancel(error)) {
            const index = this.files.indexOf(fileData);
            if (index === -1) {
              return;
            }
            this.removeFile(index);
            return;
          }

          fileData.status = FILE_STATUS.FAILED;
          fileData.error = `Error uploading a file ${fileData.name}: ${error}`;
        })
        .finally(() => {
          this.checkIfFilesReady();
        });
    },

    removeFile(index: number) {
      this.files.splice(index, 1);
    },

    onFileRemove(index: number) {
      this.removeFile(index);
      this.checkIfFilesReady();
    },

    onFileUploadCancel(file: FileUploadState) {
      if (file.status !== FILE_STATUS.PENDING || !file.abortController) {
        return;
      }

      file.abortController.abort();
    },

    onFileNameChange(file: FileMetadata & FileEditingState) {
      if (!file.newName || file.newName === file.name) {
        file.isEditing = false;
        return;
      }

      if (!file.id) {
        alert('File ID is missing');
        file.isEditing = false;
        return;
      }

      renameFile(file.id, file.newName)
        .then(() => {
          file.name = file.newName;
        })
        .catch((error) => {
          console.error('Failed to rename file:', error);
          alert('Error renaming file. Please try again.');
        })
        .finally(() => {
          file.isEditing = false;
        });
    },
  },
});
</script>

<style scoped>
.file-uploader {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 200px;
  padding: 10px;
  border: 2px dashed #ccc;
  text-align: center;
  transition: background-color 0.3s ease-in-out;
}

.file-uploader.drag-over {
  background-color: #f0f8ff;
  border-color: #007bff;
}

.file-uploader__list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  list-style-type: none;
  width: 100%;
  margin: 0;
  padding: 0;
  gap: 10px;
}

.file-uploader__file {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
}

.file-uploader__file-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-uploader__file-btns {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.file-uploader__file-progress {
  margin-top: auto;
}
</style>
